import { useEffect, useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { ContactUserTableActions, UserCard, AnalyticsInfoPopover, findDatesRange } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Table, TableColumnConfig, TableProps, Text, Button } from '@frontend/design-system';
import { APPOINTMENT_TABS } from '../../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { PatientTypeChip } from '../atoms/appointment-status-chips';

const DEFAULT_PAGE_SIZE = 5;

export interface ProspectPatientListProps {
  pageSize?: number;
  showHeader?: boolean;
  showNumRowSelector?: boolean;
  locationIdOverride?: string[];
}

export const ProspectPatientList = ({
  pageSize = DEFAULT_PAGE_SIZE,
  showHeader = true,
  showNumRowSelector = false,
  locationIdOverride,
}: ProspectPatientListProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { filters, filtersInitialized } = useAppointmentAnalyticsV2ShallowStore('filters', 'filtersInitialized');

  const [pageConfig, setPageConfig] = useState({ pageNumber: 1, pageSize });

  // Update pageConfig pageSize if prop changes
  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, pageSize }));
  }, [pageSize]);

  const dateRange = useMemo(() => {
    return findDatesRange({ offset: 7, range: 'day', type: 'past' });
  }, []);

  const locationId = useMemo(() => {
    return locationIdOverride ?? filters[APPOINTMENT_TABS.UPCOMING]?.locationId ?? [];
  }, [locationIdOverride, filters]);

  const queryRequest = useMemo(() => {
    return {
      locationId,
      startDate: dayjs(dateRange.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
      endDate: dayjs(dateRange.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
      pageSize: pageConfig.pageSize,
    };
  }, [locationId, dateRange, pageConfig.pageSize]);

  const {
    data: waitListData,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = AnalyticsGatewayV1.Queries.useGetAppointmentsWaitListQuery(queryRequest, {
    enabled: filtersInitialized && locationId.length > 0,
  });

  // Reset to page 1 when locations filter changes
  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, pageNumber: 1 }));
  }, [locationId]);

  // Fetch next page when navigating forward to an unloaded page
  useEffect(() => {
    if (!waitListData?.pages[pageConfig.pageNumber - 1]) {
      fetchNextPage();
    }
  }, [waitListData?.pages, fetchNextPage, pageConfig.pageNumber]);

  const items = waitListData?.pages[pageConfig.pageNumber - 1]?.data ?? [];

  const getHasNext = () => {
    const pageData = waitListData?.pages[pageConfig.pageNumber - 1]?.data ?? [];
    return hasNextPage && pageData.length === pageConfig.pageSize;
  };

  const colConfig: TableColumnConfig<any>[] = [
    {
      id: 'patientName',
      Header: t('Contact'),
      accessor: (item) => item,
      disableColumnFilter: true,
      cellRenderer: (value: any) => {
        return (
          <UserCard
            variant='compact'
            lastName={value?.patientName}
            key={value?.patientName}
            openProfileOnClick
            phoneNumber={''}
            showOnlyName
            userId={value?.patientId}
            locationId={locationId[0]}
          />
        );
      },
      disableSortBy: true,
      width: 250,
      minWidth: 250,
    },
    {
      id: 'matchReason',
      Header: t('Match Reason'),
      accessor: 'matchReason',
      cellRenderer: (value: string) => (value ? `Recent ${value}` : '-'),
    },
    {
      id: 'patientType',
      Header: t('Patient Type'),
      accessor: 'patientType',
      cellRenderer: (value: string) => {
        if (!value) return '-';
        return <PatientTypeChip type={value} />;
      },
    },
    {
      Header: t('Actions'),
      accessor: (item) => item,
      cellRenderer: (item: any) => {
        return (
          <ContactUserTableActions
            personId={item.patientId ?? ''}
            personName={item.patientName}
            phoneNumber={item.phoneNumber ?? ''}
          />
        );
      },
      disableSortBy: true,
      id: 'actions',
      sticky: 'right',
      disableColumnFilter: true,
      width: 'unset',
      minWidth: 120,
    },
  ];

  const tableProps: TableProps<any> = {
    isLoading: isLoading || isFetching,
    loadingRowsCount: pageConfig.pageSize,
    data: items,
    tableInstanceId: 'prospect-patient-list-table',
    hasFilterColumns: false,
    hasResizeColumns: false,
    colConfig,
    fullHeight: false,
    isPaginated: true,
    manualPaginationConfig: {
      handleChange: (action: 'next' | 'prev') => {
        if (action === 'next') {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber + 1 }));
        } else {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber - 1 }));
        }
      },
      hasNext: getHasNext(),
      hasPrevious: pageConfig.pageNumber > 1,
      page: pageConfig.pageNumber,
      defaultRowsPerPage: pageConfig.pageSize,
      showNumRowSelector,
      onNumRowsChange: showNumRowSelector
        ? (num) => {
            setPageConfig({ pageNumber: 1, pageSize: num });
          }
        : undefined,
      rowsPerPageOptions: showNumRowSelector ? [10, 25, 50, 75, 100] : undefined,
    },
    styleConfig: {
      rows: {
        height: 52,
      },
    },
  };

  return (
    <div css={styles.container}>
      {showHeader && (
        <div css={styles.header}>
          <div css={styles.titleWrapper}>
            <Text size='large' weight='bold' color='inherit'>
              {t('Prospect Patient List')}
            </Text>
            <AnalyticsInfoPopover
              popoverCss={css`
                max-width: 300px;
              `}
            >
              {t(
                'List of patients who might be interested in scheduling an appointment to fill last minute open slots'
              )}
            </AnalyticsInfoPopover>
          </div>
          <Button
            variant='primary'
            iconName='external-link'
            label={t('View Prospect List')}
            onClick={() => {
              navigate({
                to: '/analytics/appointment',
                search: (prev: any) => ({ ...prev, showingProspectList: true }),
              });
            }}
            trackingId='view-prospect-list-click'
            css={{
              fontSize: theme.fontSize(12),
              height: theme.spacing(3.5),
            }}
          >
            {t('View Prospect List')}
          </Button>
        </div>
      )}
      <Table {...tableProps} />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `,
  header: css`
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding-bottom: ${theme.spacing(1)};
  `,
  titleWrapper: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(1)};
  `,
  infoIcon: css`
    color: ${theme.colors.neutral50};
    display: flex;
    cursor: help;
  `,
};
